import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'

import useProductPage from 'src/hooks/useProductPage'
import useDashboardsService from 'src/services/dashboardService'
import useMenusStore from 'src/stores/useMenusStore'
import useSelectedDashboardStore from 'src/stores/useSelectedDashboardStore'
import { findDashboardsOnMenusFn, setSubProductDashboardsOnMenusFn } from 'src/utils/menuUtils'
import InputModal from '../../../InputModal'
import { usePageHeaderDashboardMenuContext } from '../../PageHeaderDashboardMenuProvider'

const DASHBOARD_LABEL_MIN_LENGTH = 3
const DASHBOARD_LABEL_MAX_LENGTH = 33
const LabelErrorMessage = {
  max: 'Dashboard name must not exceed 30 characters',
  min: 'Dashboard name must exceed 2 characters',
}

const ModalRenameDashboard: FC = () => {
  const { modalOpened, setModalOpened } = usePageHeaderDashboardMenuContext()
  const { selectedDashboard, setSelectedDashboard } = useSelectedDashboardStore()
  const { program, product, code: subProductCode } = useProductPage()
  const { menus, setMenus } = useMenusStore()
  const { renameDashboard } = useDashboardsService()
  const [labelError, setLabelError] = useState(false)

  const existedDashboardNames = useMemo(() => {
    const existingDashboards = findDashboardsOnMenusFn({
      menus,
      product,
      subProductCode,
      programId: program?.toString(),
    })

    return (
      existingDashboards
        ?.filter(d => d.id !== selectedDashboard?.id)
        .map(d => d.name.toUpperCase().trim()) || []
    )
  }, [menus, product, subProductCode, selectedDashboard?.id, program])

  const labelCheck = useCallback(
    (label: string): boolean => {
      const formattedLabel = label.toUpperCase().trim()
      const sameDashboardLabel = existedDashboardNames.includes(formattedLabel)
      if (sameDashboardLabel) {
        setLabelError(true)
        toast.error('A query already has this name')
        return true
      }

      if (formattedLabel.length < DASHBOARD_LABEL_MIN_LENGTH) {
        setLabelError(true)
        toast.error(LabelErrorMessage.min)
        return true
      }

      if (formattedLabel.length > DASHBOARD_LABEL_MAX_LENGTH) {
        setLabelError(true)
        toast.error(LabelErrorMessage.max)
        return true
      }

      setLabelError(false)
      return false
    },
    [existedDashboardNames],
  )

  const [name, setName] = useState(selectedDashboard?.name)

  const handleUpdateDashboard = async () => {
    if (!selectedDashboard?.id || !name) return
    if (labelCheck(name)) return
    const label = selectedDashboard.name
    setModalOpened(null)
    return toast
      .promise(
        renameDashboard({
          id: selectedDashboard.id,
          label: name,
          programId: program?.toString(),
        }),
        {
          loading: 'Please wait...',
          success: (
            <span>
              <b>{label}</b> dashboard successfully renamed
            </span>
          ),
          error: 'An error occurred. Please retry.',
        },
        { duration: 4000 },
      )
      .then(data => {
        const dashboards = data?.data || []

        if (!dashboards?.length) {
          return
        }

        const renamedDashboard = dashboards.find(d => d.id === selectedDashboard.id)

        if (!renamedDashboard) {
          return
        }

        const oldDashboards = findDashboardsOnMenusFn({
          menus,
          product,
          subProductCode,
          programId: program?.toString(),
        })

        const newDashboards = oldDashboards.map(d => {
          if (d.id === selectedDashboard.id) {
            return { ...d, name: renamedDashboard.label }
          }
          return d
        })

        setMenus(s => ({
          ...s,
          menus: setSubProductDashboardsOnMenusFn({
            menus: s.menus,
            dashboards: newDashboards,
            subProductCode,
            programId: program?.toString(),
          }),
        }))

        setSelectedDashboard({ ...selectedDashboard, name })
      })
  }

  useEffect(() => {
    if (modalOpened) {
      setName(selectedDashboard?.name)
      setLabelError(false)
    }
  }, [selectedDashboard?.name, modalOpened])

  if (!selectedDashboard) return null

  return (
    <InputModal
      title="Rename"
      buttonLabel="Save"
      open={modalOpened === 'rename'}
      onClose={() => setModalOpened(null)}
      value={name || ''}
      onChange={evt => setName(evt.target.value)}
      submit={handleUpdateDashboard}
      hasError={labelError}
    />
  )
}

export default ModalRenameDashboard
